<template>
  <Facebook v-if="merchantCountry === MerchantCountry.MX" />
  <Alert
    v-if="showAlertPhone"
    :message="`${t('MOBILE_NUMBER')}: ` + alertMessagePhone"
    :alertType="alertTypePhone"
    :closable="alertClosablePhone"
    @onClose="dismissAlertPhone"
    :isSelfService="isSelfOnboarding"
  />

  <Alert
    v-if="showAlertSMS"
    :message="`${t('SMS.CODE')}: ` + alertMessageSMS"
    :alertType="alertTypeSMS"
    :closable="alertClosableSMS"
    @onClose="dismissAlertSMS"
    :isSelfService="isSelfOnboarding"
  />

  <Alert
    v-if="showAlertEmail"
    class="my-3"
    :message="`${t('EMAIL_LABEL')}: ` + alertMessageEmail"
    :alertType="alertTypeEmail"
    :closable="alertClosableEmail"
    @onClose="dismissAlertEmail"
    :isSelfService="isSelfOnboarding"
  />

  <Alert
    v-if="showAlertFacebook"
    class="my-3"
    :message="`${t('FACEBOOK_LABEL')}: ` + alertMessageFacebook"
    :alertType="alertTypeFacebook"
    :closable="alertClosableFacebook"
    @onClose="dismissAlertFacebook"
    :isSelfService="isSelfOnboarding"
  />

  <Alert
    :message="alertMessage"
    :alertType="alertType"
    :closable="true"
    @onClose="dismissAlert"
    :isSelfService="isSelfOnboarding"
  />

  <div v-show="loading">
    <h3 :class="{ 'fs-1': isSelfOnboarding }">{{ t("WAIT_A_MOMENT") }}</h3>
    <p :class="{ 'fs-3': isSelfOnboarding }">
      {{ t("WAIT_VALIDATION_TEXT") }}
    </p>

    <div class="spinner-wrapper">
      <Spinner />
    </div>
  </div>

  <div
    v-show="!loading"
    :class="isDirectToCustomerFlow || isEcommerceFlow ? 'container mt-2' : 'container mt-5'"
  >
    <FraudAssessmentCheck
      ref="fraudCheckRef"
      @fraud-assessment-result="handleFraudAssessmentResult"
      @loading="handleFraudAssessmentLoading"
      v-if="requiresFraudAssessment"
    />
    <div v-if="!hasFraudulentAssessment">
      <div class="row">
        <div class="col">
          <h2 :class="{ 'fs-1': isSelfOnboarding }">
            {{ t("USER_CONTACT_INFO") }}
          </h2>
          <p v-if="isSelfOnboarding" class="fs-3">
            {{ t("SELF_ONBOARDING.USER_CONTACT_INFO_HELP_TEXT") }}
          </p>
          <p v-else>
            {{ t("USER_CONTACT_INFO_HELP_TEXT") }}
          </p>
        </div>
      </div>
      <Spinner v-if="spinWhileMerchantLoading" />
      <div class="row" v-else>
        <div class="col">
          <div class="row">
            <div :class="phoneVerificationRequired ? 'col-8 col-md-6' : 'col-12 col-md-6'">
              <SimplifiedPhoneInput
                :disabled="isDisabledPhoneInput"
                :label-name="t('USER_CONTACT_PHONE_LABEL')"
                v-model="statePhoneNumber"
                ref="phoneComponent"
                :country="merchantCountry"
                input-id="phone-number"
                @validate-emit="(value) => validatePhoneFormat(value)"
              />
            </div>
            <div class="col-4 col-md-4" v-show="phoneVerificationRequired">
              <button
                id="btn-send-phone"
                class="button btn pj-wide pj-green-btn btn-phone"
                :class="{ 'pj-ss-btn mt-2 fs-3': isSelfOnboarding }"
                :disabled="!phoneButtonEnabled"
                @click.prevent="handleSendPhone"
              >
                {{ sendButtonText }}
              </button>
            </div>
          </div>
          <div class="row">
            <div class="col py-2">
              <div v-if="phoneVerificationRequired">
                <p v-if="isSelfOnboarding" class="fs-3">
                  {{ t("SELF_ONBOARDING.USER_INFO_P1") }}
                </p>
                <p v-else>
                  {{ t("USER_INFO_P1") }}
                </p>
                <p :class="{ 'fs-3': isSelfOnboarding }">
                  {{ t("USER_INFO_P2") }}
                </p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-8">
              <InputFieldConfirmationCode
                v-if="phoneVerificationRequired"
                :minLength="4"
                :maxLength="4"
                :label-name="t('CUSTOMER_CONTACT_SMS_VERIFICATION_CODE_LABEL')"
                ref="codeComponent"
                :inputValue="phoneVerificationComponent"
                @keyup="enableContinueButton"
                :disabled="enableField"
                :inputType="'number'"
                :isSelfService="isSelfOnboarding"
              />
            </div>
            <div class="input_tips" v-if="phoneVerificationRequired">
              <p :class="{ 'fs-3': isSelfOnboarding }">
                {{
                  t("CUSTOMER_CONTACT_SMS_VERIFICATION_TOOL_TIP", {
                    NUMBER: usedPhone,
                  })
                }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-md-6">
              <InputFieldEmail
                :label-name="emailLabel"
                ref="emailComponent"
                :required="isEmailRequired"
                :state-email="stateEmail"
                class="client_email"
                @validate-emit="(value) => validateEmailFormat(value)"
                @keyup="enableContinueButton"
                :isSelfService="isSelfOnboarding"
              />
            </div>
            <div class="col-4" v-show="false">
              <button
                id="btn-send-email"
                class="button btn pj-wide pj-green-btn btn-phone"
                @click.prevent="handleSendEmail"
              >
                {{ t("USER_CONTACT_SEND") }}
              </button>
            </div>
          </div>
          <IndonesiaInfo
            v-if="isIndonesiaSaleFlow()"
            :initialData="indonesiaInitialData"
            @update:data="handleIndonesiaInfo"
          />
          <div
            class="row"
            v-if="useFacebookFraudDetection || facebookFraudDetectOptional"
          >
            <div class="col">
              <div class="fb-label">{{ t("CONNECT_WITH_YOUR_FACEBOOK") }}</div>
              <div class="fb-accessed" v-if="facebookAccessed">
                <i class="fa fa-check" aria-hidden="true"></i
                >{{ t("FACEBOOK_ACCESSED") }}
              </div>
              <div v-else>
                <button
                  class="button btn pj-wide fb-login"
                  @click="logInWithFacebook"
                >
                  <img
                    class="fb-login-logo"
                    :src="`${useAsset('img/facebook-circle-white-icon.svg')}`"
                    alt="facebook logo"
                  /><span class="fb-inner"
                    >&nbsp;&nbsp; {{ t("CONTINUE_WITH_FACEBOOK") }}</span
                  >
                </button>
              </div>
              <div class="input_tips">
                <p>{{ t("FACEBOOK_FRAUD_TEXT") }}</p>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-8">
              <div
                id="source-of-founds-wrapper"
                v-if="shouldDisplaySourceOfFund"
              >
                <div class="mb-2">
                  {{ t("POLITICALLY_EXPOSED_PERSON_LABEL") }}
                </div>
                <ToggleButton
                  :left-label="t('YES')"
                  :right-label="t('NO')"
                  :default-active-option="0"
                  @update-switch="updatePoliticallyExposedPerson"
                />
                <Dropdown
                  v-if="shouldDisplaySourceOfFundDropdown"
                  :dropdown-Label="t('SOURCE_OF_FUNDS_LABEL')"
                  :options="sourceOfFundsDropdown"
                  v-model="selectedSourceOfFund"
                  @update:modelValue="setSelectedSourceOfFundDropdown"
                  :key="componentKeySourceOfFund"
                  data-testid="source_of_funds"
                  ref="sourceOfFundsRef"
                  placeholder="--"
                />
                <InputFieldTextarea
                  v-else
                  class="mb-4"
                  ref="sourceOfFundsRef"
                  v-model="sourceOfFunds"
                  :label-name="t('SOURCE_OF_FUNDS_LABEL')"
                  @focusout="validateSourceOfFounds"
                />
              </div>
              <OccupationMaritalStatusInfo
                v-if="isPeruSaleFlow()"
                @update:data="handleExtraInfo"
              />
              <div v-if="customerAddressIsRequired">
                <Alert
                  v-if="addressError"
                  :message="addressErrorMessage"
                  :alertType="addressAlertType"
                  :closable="alertClosable"
                  @onClose="dismissAddressError"
                />
                <CustomerAddress
                  @customer-address="updateCustomerAddress"
                  @block-continue-for-ceara="handleBlockContinueForCeara"
                  :showPostalCode="showPostalCode"
                  :address="address"
                />
              </div>
              <div v-if="creditCheckIsRequired">
                <InputFieldCheckbox
                  :id="'credit-check-checkbox'"
                  :label="creditCheckPermissionLabel"
                  :disabled="creditCheckDisabled"
                  :isChecked="creditCheckChecked"
                  @on-checkbox-click="onCreditCheckClick"
                />
                <a
                  v-if="isPhilippinesSaleFlow()"
                  href="https://docs.google.com/document/d/1PEYXH_MZY-ZCWS1hJfa9cP9dUPOGKvO4s6XQWODHtoQ/edit"
                  target="_blank"
                  >{{ t("CONSENT_FORM") }}</a
                >
              </div>
            </div>
          </div>
          <ButtonContinue
            id="submit"
            :isEnabled="isEnableContinueButton"
            :label="t('CONTINUE_BUTTON')"
            @click="continueButton"
            :class="{ 'pj-ss-btn fs-3': isSelfOnboarding }"
          />
          <PreviousStepButton />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  WritableComputedRef,
  ComputedRef,
  onMounted,
  watch,
  nextTick,
} from "vue";
import { useStore } from "vuex";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import InputFieldEmail from "@/apps/merchant3/shared-components/input-fields/InputFieldEmail.vue";
import SimplifiedPhoneInput from "@/apps/merchant3/shared-components/input-fields/SimplifiedPhoneInput.vue";
import InputFieldConfirmationCode from "@/apps/merchant3/shared-components/input-fields/InputFieldConfirmationCode.vue";
import Dropdown from "@/apps/merchant3/shared-components/ui/Dropdown.vue";
import { IOptionDropdown } from "@/types/dropdown";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import { CartMilestones } from "@/apps/merchant3/types/CartMilestones";
import { createCartMilestone } from "@/apps/merchant3/services/cart-milestones.service";
import {
  recordEmail,
  recordPhone,
  verifyPhone,
} from "@/apps/merchant3/services/customerContactInfo.service";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useRouter } from "vue-router";
import { AxiosError, AxiosResponse } from "axios";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import useMerchantPersistState from "@/apps/merchant3/hooks/useMerchantPersistState";
import { getCartStatus } from "@/apps/merchant3/services/carts.service";
import ICartStatus from "@/apps/merchant3/models/ICartStatus";
import IErrorResponse from "@/apps/merchant3/types/IErrorResponse";
import useIncodeWebFlow from "@/apps/merchant3/hooks/useIncodeWebFlow";
import useIntervalCustomerState from "@/apps/merchant3/hooks/useIntervalCustomerState";
import { getAPIErrorMessage } from "@/apps/merchant3/shared-helpers/APIErrorMessages";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import { saveFacebookData } from "@/apps/merchant3/services/customerContactInfo.service";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";

import cancelCartState from "@/apps/merchant3/hooks/cancelCartState";
import Facebook from "@/apps/merchant3/components/Facebook.vue";
import { incodeUpdateUuid } from "@/apps/merchant3/services/incode.service";
import { RoutesPageSteps } from "@/apps/merchant3/router/RoutesPageSteps";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import { openChatAfterSeconds } from "@/apps/merchant3/helpers/chatBotPopup";
import { recordAddress } from "@/apps/merchant3/services/registration.service";
import CustomerAddress from "@/apps/merchant3/shared-components/common/CustomerAddress.vue";
import { GreetingKey } from "@/apps/merchant3/types/ChatBotGreetingKeys";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import { PermissionType } from "@/apps/merchant3/types/CreditCheckPermission";
import { MerchantCountry } from "@/types/merchant";
import ToggleButton from "@/apps/merchant3/shared-components/common/ToggleButton.vue";
import InputFieldTextarea from "@/apps/merchant3/shared-components/input-fields/InputFieldTextarea.vue";
import { ProductVersion } from "@/apps/merchant3/types/ProductVersion";
import PreviousStepButton from "@/apps/merchant3/shared-components/ui/PreviousStepButton.vue";
import ICustomerAddress from "@/apps/merchant3/types/ICustomerAddress";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import {
  isBrazilSaleFlow,
  isColombiaSaleFlow,
  isDirectToCustomerFlow,
  isEcommerceFlow,
  isEcuadorSaleFlow,
  isIndonesiaSaleFlow,
  isPanamaSaleFlow,
  isPeruSaleFlow,
  isPhilippinesSaleFlow,
  isRentalFlow,
  isSouthAfricaSaleFlow,
} from "@/apps/merchant3/helpers/checkoutFlows";
import InputFieldCheckbox from "@/apps/merchant3/shared-components/input-fields/InputFieldCheckbox.vue";
import { authorizeCreditCheck } from "@/apps/merchant3/services/creditCheckPermission.service";
import { isColombiaSelfServiceFlow } from "@/apps/merchant3/helpers/self-service";
import { logEcommerceEvent } from "@/apps/merchant3/helpers/offers/ecommerce-event-log";
import { createCustomerNote } from "@/apps/merchant3/services/customers.service";
import {
  ICustomerNote,
  IOccupationMaritalStatusInfo,
} from "@/apps/merchant3/types/customer-registration/customer-contact-info";
import OccupationMaritalStatusInfo from "@/apps/merchant3/components/customer-registration/customer-contact-info/countries-extra-info/OccupationMaritalStatusInfo.vue";
import IndonesiaInfo from "@/apps/merchant3/components/customer-registration/customer-contact-info/countries-extra-info/IndonesiaInfo.vue";

const indonesiaInfoData = ref({ isValid: false, data: {} });
const indonesiaInitialData = ref<Record<string, string>>({});
const handleIndonesiaInfo = (data) => {
  indonesiaInfoData.value = data;
  enableContinueButton();
};

import { enablePixel } from "@/apps/merchant3/helpers/pixel.helper";
import {
  PersonAttributesData,
  storePersonAttributes,
  getPersonAttributes,
} from "@/apps/merchant3/services/persons.service";
import FraudAssessmentCheck from "@/apps/merchant3/components/customer-registration/customer-contact-info/FraudAssessmentCheck.vue";
import { getOffersRouteName } from "@/apps/merchant3/helpers/getOffersRouteName";
import { getCountryCode } from "@/apps/merchant3/helpers/flowType";

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const address = computed(() => {
  const { neighborhood, city, state, street1, postalCode } =
    store.state.customer;
  return { neighborhood, city, state, street1, postalCode };
});
const creditCheckPermittedByEsign = computed(
  () => store.state.carts.creditCheckPermissionType === PermissionType.E_SIGN
);
const creditCheckPermittedSMS = computed(
  () => store.state.carts.creditCheckPermissionType === PermissionType.SMS
);
const shouldDisplaySourceOfFund = computed(() => {
  return isColombiaSaleFlow() || isPanamaSaleFlow() || isPeruSaleFlow();
});
const shouldDisplaySourceOfFundDropdown = computed(() => {
  return isPanamaSaleFlow() || isPeruSaleFlow();
});
const phoneComponent = ref();
const usedPhone = ref("");
const emailComponent = ref();
const codeComponent = ref();
const facebookAccessed = ref();
const phoneVerificationComponent = ref("");
const sendButtonText = ref(t("USER_CONTACT_SEND"));
const loading = ref(true);
const spinWhileMerchantLoading = ref(true);
const merchantCountry = computed(
  () => store?.state.merchant?.merchant?.country || ""
);
const isEmailRequired = computed(() => {
  return (
    isBrazilSaleFlow() ||
    isColombiaSaleFlow() ||
    isEcuadorSaleFlow() ||
    isIndonesiaSaleFlow() ||
    isPanamaSaleFlow() ||
    isPeruSaleFlow() ||
    isSouthAfricaSaleFlow()
  );
});
const isSelfOnboarding = computed(() => {
  return store.state.carts?.productVersion == ProductVersion.M3_SELF_ONBOARDING;
});
const showPostalCode = computed(() => {
  return (
    merchantCountry.value === MerchantCountry.ZA ||
    isColombiaSaleFlow() ||
    isPanamaSaleFlow() ||
    isBrazilSaleFlow()
  );
});

const selectedSourceOfFund = ref();
const componentKeySourceOfFund = ref(0);
const sourceOfFundsDropdown: IOptionDropdown[] = [
  {
    name: "Haberes",
    code: "Haberes",
  },
  {
    name: "Ahorros",
    code: "Ahorros",
  },
  {
    name: "Préstamo de Familiar",
    code: "Préstamo de Familiar",
  },
  {
    name: "Comercio",
    code: "Comercio",
  },
  {
    name: "Otros",
    code: "Otros",
  },
];
const setSelectedSourceOfFundDropdown = (value: IOptionDropdown) => {
  sourceOfFunds.value = value.name;
  enableContinueButton();
};

const emailLabel = computed(() => {
  let label: string;
  if (isSelfOnboarding.value) {
    label = t("SELF_ONBOARDING.CUSTOMER_CONTACT_INFO_EMAIL_LABEL");
  } else {
    label = t("CUSTOMER_CONTACT_INFO_EMAIL_LABEL");
  }
  if (!isEmailRequired.value) {
    label += " (" + t("OPTIONAL") + ")";
  }
  return label;
});
document.title = "PayJoy - " + t("CUSTOMER_REGISTRATION");
const phoneVerificationRequired = computed(() => {
  const merchantFlags = store.state.merchant?.merchantFlags
    ? JSON.parse(JSON.stringify(store.state.merchant?.merchantFlags))
    : [];

  if (merchantFlags.includes("NoPinVerification")) {
    return false;
  }

  if (isEcommerceFlow.value) {
    return !!store.state.carts?.requiresOTP;
  }

  return !creditCheckPermittedByEsign.value;
});
const alertTypePhone = ref("");
const alertMessagePhone = ref("");
const showAlertPhone = ref(false);
const alertClosablePhone = ref(true);
const isEnableNextStep = ref(false);
const isPhoneAlreadyVerified = ref(false);
const phoneValidated = ref(false);
const emailValidated = ref(false);
const phoneButtonEnabled = ref(true);
const enableField = ref(true);
const politicallyExposedPerson = ref();
const sourceOfFunds = ref("");
const sourceOfFundsRef = ref();
const customerIsDuplicate = computed(() =>
  store.state.customer?.notes?.includes("duplicate")
);
const occupationMaritalStatusInfo = ref<IOccupationMaritalStatusInfo | null>(
  null
);
const handleExtraInfo = (data: IOccupationMaritalStatusInfo) => {
  occupationMaritalStatusInfo.value = data;
};

const isEnableContinueButton = computed(() => {
  const phoneIsValid = phoneVerificationRequired.value
    ? isEnableNextStep.value || isPhoneAlreadyVerified.value
    : phoneValidated.value;
  const emailIsValid = isEmailRequired.value ? emailValidated.value : true;
  const isPoliticallyExposedPersonValid =
    politicallyExposedPerson.value !== undefined && sourceOfFunds.value !== "";

  if (isColombiaSaleFlow()) {
    return (
      phoneIsValid &&
      emailIsValid &&
      isPoliticallyExposedPersonValid &&
      addressIsValid.value
    );
  }
  if (isPeruSaleFlow()) {
    const extraInfoIsValid = occupationMaritalStatusInfo.value?.isValid;

    return (
      (phoneIsValid || creditCheckPermittedSMS.value) &&
      emailIsValid &&
      isPoliticallyExposedPersonValid &&
      sourceOfFunds.value !== "" &&
      extraInfoIsValid
    );
  }

  if (isBrazilSaleFlow()) {
    return (
      phoneIsValid &&
      emailIsValid &&
      addressIsValid.value &&
      creditCheckChecked.value &&
      !shouldBlockContinueForCeara.value
    );
  }

  if (creditCheckPermittedByEsign.value && phoneIsValid) {
    return true;
  }

  if (merchantCountry.value === MerchantCountry.MX) {
    return creditCheckPermittedSMS.value ? true : phoneIsValid && emailIsValid;
  }

  if (isIndonesiaSaleFlow()) {
    return (
      (phoneIsValid || creditCheckPermittedSMS.value) &&
      emailIsValid &&
      indonesiaInfoData.value.isValid
    );
  }

  return creditCheckPermittedSMS.value
    ? true
    : phoneIsValid && emailIsValid && addressIsValid.value;
});

// verify phone SMS code warning Alert
const alertTypeSMS = ref("");
const alertMessageSMS = ref("");
const showAlertSMS = ref(false);
const alertClosableSMS = ref(true);
// recordEmail warning Alert
const alertTypeEmail = ref("");
const alertMessageEmail = ref("");
const showAlertEmail = ref(false);
const alertClosableEmail = ref(true);
const readyToCheckPjVerified = ref(false);
const emailRecorded = ref(false);
// verify phone SMS code warning Alert
const alertTypeFacebook = ref("");
const alertMessageFacebook = ref("");
const showAlertFacebook = ref(false);
const alertClosableFacebook = ref(true);
// standard warning Alert
const alertType = ref("alert-warning");
const alertMessage = ref("");
const showAlert = ref(false);
// Address
const addressError = ref(false);
const addressErrorMessage = ref(t("ERROR_SOMETHING_GOES_WRONG"));
const addressAlertType = ref("");
const alertClosable = ref(true);
const newCustomerAddress = ref();
const addressRecorded = ref(false);
const shouldBlockContinueForCeara = ref(false);
const timeoutErrorCode = 10283;
const useFacebookFraudDetection = computed(() =>
  store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.USE_FACEBOOK_FRAUD_DETECTION
  )
);
const facebookFraudDetectOptional = computed(() =>
  store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.FACEBOOK_FRAUD_DETECT_OPTIONAL
  )
);
const colombiaAutoApproval = computed(() =>
  store.state.merchant?.merchantFlags?.includes(
    MerchantFlags.COLOMBIA_AUTO_LOADING
  )
);

const updatePageStatus = () => {
  spinWhileMerchantLoading.value = false;
  return true;
};
const cartTag = computed(() => {
  return store.state.carts.cartTag;
});
const validatePhoneFormat = (value) => {
  phoneValidated.value = value;
};
const validateEmailFormat = (value) => {
  emailValidated.value = value;
};

const isWebFlow = useIncodeWebFlow();
const useChatPopup = computed(() => {
  return (
    store.state.merchant?.merchant?.country === "ZA" &&
    store.state.merchant?.merchantFlags?.includes(
      MerchantFlags.USE_ADA_CHATBOT_AUTO_TRIGGER
    )
  );
});

const customerAddressIsRequired = computed(() => {
  return (
    isEcuadorSaleFlow() ||
    isColombiaSaleFlow() ||
    isSouthAfricaSaleFlow() ||
    isPanamaSaleFlow() ||
    isBrazilSaleFlow()
  );
});

const addressIsValid = computed(() => {
  if (isEcuadorSaleFlow()) {
    return !!(
      newCustomerAddress.value.city &&
      newCustomerAddress.value.address &&
      newCustomerAddress.value.neighborhood &&
      newCustomerAddress.value.state
    );
  } else if (
    isSouthAfricaSaleFlow() ||
    isColombiaSaleFlow() ||
    isBrazilSaleFlow() ||
    isPanamaSaleFlow()
  ) {
    return !!(
      newCustomerAddress.value.city &&
      newCustomerAddress.value.address &&
      newCustomerAddress.value.neighborhood &&
      newCustomerAddress.value.state &&
      newCustomerAddress.value.postalCode
    );
  } else {
    return true;
  }
});

const cartInformationLoaded = () => {
  loading.value = false;
  if (isSelfOnboarding.value) {
    openChatAfterSeconds(240, GreetingKey.SELF_ONBOARDING);
    return true;
  }
  if (useChatPopup.value) {
    openChatAfterSeconds(300);
  }
  return true;
};
const isDisabledPhoneInput = ref(false);
onMounted(() => {
  useMerchantPersistState(() => {
    enablePixel(
      process.env.VUE_APP_PIXEL_ID,
      Number(store.state.merchant?.merchant?.merchantId)
    ); // reinit in case SMILE breaks it
    return updatePageStatus();
  });
  useCartPersistState(cartInformationLoaded);
  updateIncodeUuid();
  logEcommerceEvent(EventLogs.Events.ECOMMERCE_CONTACT_INFO);
});

const fetchIndonesiaPersonAttributes = () => {
  if (isIndonesiaSaleFlow() && store.state.carts.cartTag) {
    getPersonAttributes(store.state.carts.cartTag)
      .then((response) => {
        const attrs = response.data?.personAttributes;
        if (attrs?.length) {
          const mapped: Record<string, string> = {};
          for (const attr of attrs) {
            mapped[attr.slug] = attr.value;
          }
          indonesiaInitialData.value = mapped;
        }
      })
      .catch(() => {
        // Silently fail - fields just won't be pre-filled
      });
  }
};

const handleCustomerStateInterval = () => {
  useIntervalCustomerState(
    0,
    () => {
      store.dispatch("carts/setStep", RoutesPageSteps.CustomerContactInfo);
      fetchIndonesiaPersonAttributes();
      if (creditCheckPermittedSMS.value) {
        isDisabledPhoneInput.value = true;
        phoneButtonEnabled.value = false;
      }
      createCartMilestone(
        cartTag.value,
        CartMilestones.CUSTOMER_CONTEACT_INFO_VIEW_LOADED
      );
      if (isEcommerceFlow.value) {
        getCartStatus(store.state.carts.cartTag)
          .then(async (response: AxiosResponse<ICartStatus>) => {
            const cartStatus: ICartStatus = response.data;
            if (cartStatus.terminateSale) {
              await logEcommerceEvent(
                EventLogs.Events.ECOMMERCE_CART_TERMINATE_SALE_CUSTOMER_INFO
              );
              await logEcommerceEvent(EventLogs.Events.ECOMMERCE_CONTACT_INFO);
              await router.push({
                name: RouteName.M3RejectedOfferEcommerce,
                params: { countryCode: getCountryCode() },
              });
            }
          })
          .catch((error: AxiosError<IErrorResponse>) => {
            const errorResponse = error.response?.data;
            if (errorResponse) {
              setErrorMessage(getAPIErrorMessage(errorResponse, t));
            }
          });
      }
    },
    store
  );
};

const userTag = computed(() => store.state.carts?.userTag);
watch(
  userTag,
  (newUserTag, oldUserTag) => {
    if (newUserTag && newUserTag !== oldUserTag) {
      handleCustomerStateInterval();
    }
  },
  { immediate: false }
);

const statePhoneNumber: WritableComputedRef<string | null> = computed({
  get() {
    // Return null instead of empty string when phone is not set
    // This allows InputFieldPhone to auto-populate the country code
    return store.state.customer.phone || null;
  },
  set(newValue) {
    store.dispatch("customer/setCustomerPhone", newValue);
  },
});

const stateEmail: WritableComputedRef<string> = computed({
  get() {
    return store.state.customer.email || "";
  },
  set(newValue) {
    store.dispatch("person/setEmail", newValue);
  },
});

watch(
  stateEmail,
  (newEmail) => {
    if (
      newEmail &&
      emailComponent.value &&
      // Ensures the validate method exists for test compatibility
      typeof emailComponent.value.validate === "function"
    ) {
      nextTick(() => {
        const isValid = emailComponent.value.validate();
        validateEmailFormat(isValid);
      });
    }
  },
  { immediate: true }
);

const dismissAlertPhone = () => {
  showAlertPhone.value = false;
};
const dismissAlertSMS = () => {
  showAlertSMS.value = false;
};
const dismissAlertEmail = () => {
  showAlertEmail.value = false;
};
const dismissAlertFacebook = () => {
  showAlertFacebook.value = false;
};
const dismissAlert = () => {
  showAlert.value = false;
};
const enableContinueButton = () => {
  const phoneIsValid = phoneComponent.value?.validate() ?? false;
  const customerHasAPhone = !!store.state.customer.phone;
  const emailIsValid = emailComponent.value?.validate() ?? false;
  isEnableNextStep.value = false;
  if (isPhoneAlreadyVerified.value) {
    isEnableNextStep.value = true;
    return;
  }
  if (phoneVerificationRequired.value) {
    if (!enableField.value) {
      const pinIsValid = codeComponent.value?.validate() ?? false;
      updatePhoneVerificationComponent(pinIsValid);
      isEnableNextStep.value =
        customerHasAPhone && phoneIsValid && pinIsValid && emailIsValid;
    }
  } else {
    isEnableNextStep.value = customerHasAPhone && phoneIsValid && emailIsValid;
  }
};
const updatePhoneVerificationComponent = (pinIsValid: boolean) => {
  const newValue = codeComponent.value.getInputFieldValue();
  if (newValue === phoneVerificationComponent.value) return;
  phoneVerificationComponent.value = newValue;
  if (pinIsValid && newValue?.length >= 4) {
    logEvent(EventLogs.Events.CUSTOMER_ENTERED_A_PIN_TO_VERIFY_THEIR_PHONE, {
      pinValue: codeComponent.value.formConfirmationCode,
    });
  }
};
const setErrorMessage = (message: string) => {
  if (message) {
    alertType.value = "alert-danger";
    alertMessage.value = message;
    showAlert.value = true;
  }
};
const updateIncodeUuid = () => {
  if (isWebFlow()) {
    incodeUpdateUuid(cartTag.value)
      .then(() => {
        logEvent(EventLogs.Events.INCODE_WITH_FINAL_UUID_RECORDED);
        logAction(ActionLogs.Events.INCODE_UUID_UPDATED);
      })
      .catch(() => {
        setErrorMessage("Failed to update Incode UUID");
      });
  }
};

const allowResendPhone = (): void => {
  phoneButtonEnabled.value = false;
  let countdownSeconds = 60;
  sendButtonText.value = t("USER_CONTACT_RESEND", { countdownSeconds });
  let resendPhoneTimer = setInterval(function () {
    if (countdownSeconds === 0) {
      sendButtonText.value = t("USER_CONTACT_SEND");
      phoneButtonEnabled.value = true;
      clearInterval(resendPhoneTimer);
    } else {
      countdownSeconds -= 1;
      sendButtonText.value = t("USER_CONTACT_RESEND", { countdownSeconds });
    }
  }, 1000);
};

const handleSendPhone = (): void => {
  isPhoneAlreadyVerified.value = false;
  usedPhone.value = phoneComponent.value.getInputFieldValue();
  const phoneValid = phoneComponent.value.validate();
  phoneValid &&
    recordPhone(store.state.carts.cartTag, usedPhone.value)
      .then((response: AxiosResponse) => {
        logEvent(EventLogs.Events.CUSTOMER_PHONE_WAS_FILLED_IN_AND_VERIFIED, {
          phoneNumber: usedPhone.value,
        });
        const { customer } = response.data;
        store.dispatch("customer/setCustomer", customer);
        enableField.value = false;
        if (response.data?.warning) {
          isPhoneAlreadyVerified.value = true;
          isEnableNextStep.value = true;
          checkFraud();
        } else {
          alertMessagePhone.value = t("VERIFICATION_PHONE_CODE_SENT");
          showAlertPhone.value = true;
          alertTypePhone.value = "alert-success";
        }
        allowResendPhone();
      })
      .catch((error: AxiosError) => {
        phoneVerificationComponent.value = "";
        enableField.value = true;
        isEnableNextStep.value = false;
        if (error?.response?.data.errorCode === timeoutErrorCode) {
          clearCartAndGoToWelcome();
        }
        alertMessagePhone.value = getAPIErrorMessage(error.response?.data, t);
        alertTypePhone.value = "alert-danger";
        showAlertPhone.value = true;
      });
};
const validatePhone = (): boolean => {
  return phoneComponent.value?.getInputFieldValue() !== "+";
};
const validateEmail = (): boolean => {
  return emailComponent.value.validate();
};
const handleSendEmail = (): void => {
  phoneComponent.value.validate() &&
    emailComponent.value.validate() &&
    recordEmail(
      store.state.carts.cartTag,
      emailComponent.value.getInputFieldValue()
    )
      .then((response: AxiosResponse) => {
        store.dispatch("customer/setCustomer", response.data);
        emailRecorded.value = true;
      })
      .catch((error: AxiosError) => {
        if (error?.response?.data.errorCode === timeoutErrorCode) {
          clearCartAndGoToWelcome();
        }
        alertTypeEmail.value = "alert-danger";
        alertMessageEmail.value = getAPIErrorMessage(error.response?.data, t);
        showAlertEmail.value = true;
      });
};
const clearCartAndGoToWelcome = () => {
  store.state.person.userData.termsAndConditionsAccept = null;
  store.state.person.userData.privacyPolicyAccept = null;
  store.state.person.userData.frontImageURL = null;
  store.state.person.userData.backImageURL = null;
  store.state.person.userData.selfieImageURL = null;
  store.dispatch("customer/resetCustomer");
  store.dispatch("carts/resetCart");
  store.dispatch("offers/resetOffers");
  cancelCartState();
  router.push({ name: RouteName.M3Welcome, params: { countryCode: getCountryCode() } });
};
const checkFraud = () => {
  logEvent(EventLogs.Events.FRAUD_CHECKED, {
    isFraud: store.state.customer.isFraud,
  });
  logAction(ActionLogs.Events.CHECK_FRAUD, {
    isFraud: store.state.customer.isFraud,
  });
  if (store.state.customer.isFraud !== 1) {
    checkForm();
  }
};

const resetAlerts = () => {
  alertMessageFacebook.value = "";
  showAlertFacebook.value = false;

  alertMessageEmail.value = "";
  showAlertEmail.value = false;

  alertMessageSMS.value = "";
  showAlertSMS.value = false;

  alertMessagePhone.value = "";
  showAlertPhone.value = false;

  addressError.value = false;
  addressErrorMessage.value = "";

  showAlert.value = false;
  alertMessage.value = "";
};

const continueButton = async (event) => {
  resetAlerts();
  if (!emailComponent.value.validate()) {
    return;
  }

  if (creditCheckPermittedByEsign.value) {
    const phoneEmail = {
      phoneNumber: phoneComponent.value.getInputFieldValue(),
      email: emailComponent.value.getInputFieldValue() || "",
    };
    await logEvent(
      EventLogs.Events.CREDIT_CHECK_PERMITTED_CONFIRMED_PHONE_NO_PIN,
      {
        ...phoneEmail,
      }
    );
  }

  createCartMilestone(
    cartTag.value,
    CartMilestones.CUSTOMER_CLICKED_CONTINUE_ON_CONTECT_INFO_VIEW
  );

  if (isIndonesiaSaleFlow() && store.state.customer.personUuid) {
    const payload: PersonAttributesData = {
      personUuid: store.state.customer.personUuid,
      personAttributes: indonesiaInfoData.value.data,
    };

    await storePersonAttributes(payload);
  }

  if (creditCheckPermittedSMS.value) {
    if (emailComponent.value.getInputFieldValue()) {
      handleSendEmail();
    }
    return router.push({ name: getOffersRouteName(), params: { countryCode: getCountryCode() } });
  }

  if (phoneVerificationRequired.value) {
    checkFraud();
  } else {
    handleSendPhone();
  }

  if (emailComponent.value.getInputFieldValue()) {
    handleSendEmail();
  }

  event?.target?.blur();
};

// TODO: Refactor this to standarize flows per country, improving readability and efficiency - CHECK 4636
const handleCountrySpecificSettings = async () => {
  if (
    requiresFraudAssessment.value &&
    fraudAssessmentResult.value !== "VALID"
  ) {
    if (fraudAssessmentResult.value === null) {
      fraudCheckRef.value?.start();
    }
    return;
  }

  if (isPeruSaleFlow()) {
    try {
      logAction(ActionLogs.Events.CUSTOMER_CONTACT_INFO_ENTERED, {
        politicallyExposedPerson: politicallyExposedPerson.value,
        sourceOfFunds: sourceOfFunds.value,
      });

      const userId = store.state.carts?.userId ?? null;

      if (userId !== null && occupationMaritalStatusInfo.value) {
        const payload: ICustomerNote = {
          userId: userId,
          noteData: occupationMaritalStatusInfo.value.data as Record<
            string,
            string
          >,
        };

        createCustomerNote(payload).catch((error: AxiosError) => {
          if (error?.response?.data.errorCode === timeoutErrorCode) {
            clearCartAndGoToWelcome();
          }
        });
      }

      if (
        politicallyExposedPerson.value ||
        occupationMaritalStatusInfo.value?.data.actingOnBehalfOfAThirdParty ===
          "yes"
      ) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );
        return router.push({ name: RouteName.M3CustomerDeclinedValidation, params: { countryCode: getCountryCode() } });
      }

      if (readyToCheckPjVerified.value && occupationMaritalStatusInfo.value) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );

        return router.push({
          name: RouteName.M3CXWaitValidationView,
          params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
        });
      }
    } catch (error) {
      return;
    }

    return;
  }

  // TODO: important! remove the if statement with all its logic once colombiaAutoApproval is removed!
  if (
    (isColombiaSaleFlow() && !colombiaAutoApproval.value) ||
    isPanamaSaleFlow()
  ) {
    try {
      await submitAddress();
      if (isPanamaSaleFlow()) {
        logAction(ActionLogs.Events.CUSTOMER_CONTACT_INFO_ENTERED, {
          politicallyExposedPerson: politicallyExposedPerson.value,
          sourceOfFunds: sourceOfFunds.value,
        });
      } else {
        logAction(ActionLogs.Events.COLOMBIA_CUSTOMER_CONTACT_INFO_ENTERED, {
          politicallyExposedPerson: politicallyExposedPerson.value,
          sourceOfFunds: sourceOfFunds.value,
        });
      }
      if (politicallyExposedPerson.value) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );
        return router.push({ name: RouteName.M3CustomerDeclinedValidation, params: { countryCode: getCountryCode() } });
      }

      if (readyToCheckPjVerified.value && addressRecorded.value) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );
        return router.push({
          name: RouteName.M3CXWaitValidationView,
          params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
        });
      }
    } catch (error) {
      return;
    }

    return;
  }

  if (customerAddressIsRequired.value) {
    try {
      await submitAddress();
      if (readyToCheckPjVerified.value && addressRecorded.value) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );
        router.push({
          name: RouteName.M3CXWaitValidationView,
          params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
        });
      }
    } catch (error) {
      return;
    }
  } else if (merchantCountry.value === MerchantCountry.ZA) {
    if (readyToCheckPjVerified.value) {
      await logEcommerceEvent(EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE);
      router.push({
        name: RouteName.M3CXWaitValidationView,
        params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
      });
    }
  } else if (readyToCheckPjVerified.value) {
    if (
      merchantCountry.value === MerchantCountry.MX ||
      merchantCountry.value === MerchantCountry.PH ||
      (merchantCountry.value === MerchantCountry.CO &&
        colombiaAutoApproval.value)
    ) {
      if (merchantCountry.value === MerchantCountry.CO) {
        try {
          await submitAddress();
          logAction(ActionLogs.Events.COLOMBIA_CUSTOMER_CONTACT_INFO_ENTERED, {
            politicallyExposedPerson: politicallyExposedPerson.value,
            sourceOfFunds: sourceOfFunds.value,
          });

          if (politicallyExposedPerson.value) {
            await logEcommerceEvent(
              EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
            );
            return router.push({
              name: RouteName.M3CustomerDeclinedValidation,
              params: { countryCode: getCountryCode() },
            });
          }
        } catch (error) {
          return;
        }

        if (!addressRecorded.value) {
          return;
        }
      }

      getCartStatus(store.state.carts.cartTag)
        .then((response: AxiosResponse<ICartStatus>) => {
          const cartStatus: ICartStatus = response.data;
          logEvent(EventLogs.Events.CHECK_PJVERIFIED, {
            idApproved: cartStatus.idApproved,
            idVerified: cartStatus.idVerified,
            duplicate: customerIsDuplicate.value,
          });

          logAction(ActionLogs.Events.CHECK_PJVERIFIED, {
            idApproved: cartStatus.idApproved,
            idVerified: cartStatus.idVerified,
            duplicate: customerIsDuplicate.value,
          });

          if (
            cartStatus.idApproved &&
            cartStatus.idVerified &&
            !cartStatus.terminateSale
          ) {
            logEvent(
              EventLogs.Events.CART_STATUS_FOR_PRINGING_ELIGIBILITY_RETURNED,
              {
                idApproved: cartStatus.idApproved,
                idVerified: cartStatus.idVerified,
                terminateSale: cartStatus.terminateSale,
                duplicate: customerIsDuplicate.value,
              }
            );

            logAction(ActionLogs.Events.PRICING_ELIGIBILITY_RESULT, {
              idApproved: cartStatus.idApproved,
              idVerified: cartStatus.idVerified,
              terminateSale: cartStatus.terminateSale,
              duplicate: customerIsDuplicate.value,
            });
            if (customerIsDuplicate.value) {
              logEcommerceEvent(
                EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
              );
              router.push({ name: RouteName.M3CXWaitValidationView, params: { countryCode: getCountryCode() } });
            } else {
              logEcommerceEvent(
                EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
              );
              router.push({ name: getOffersRouteName(), params: { countryCode: getCountryCode() } });
            }
          } else if (merchantCountry.value === MerchantCountry.PH) {
            // Maybe we will also need this for PA, PE and BR
            logEcommerceEvent(EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE);
            router.push({
              name: RouteName.M3CXWaitValidationView,
              params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
            });
          } else {
            readyToCheckPjVerified.value = false;
            sessionStorage.setItem("PJ_cartStatus", JSON.stringify(cartStatus));
            router.push({
              name: RouteName.M3CustomerDeclinedValidation,
              params: { countryCode: getCountryCode() },
            });
          }
        })
        .catch((error: AxiosError<IErrorResponse>) => {
          const errorResponse = error.response?.data;
          if (errorResponse) {
            readyToCheckPjVerified.value = false;
            setErrorMessage(getAPIErrorMessage(errorResponse, t));
          }
        });
    } else {
      logEvent(EventLogs.Events.CX_VALIDATION_REQUESTED);
      logAction(ActionLogs.Events.CX_VALIDATION);

      if (!isWebFlow()) {
        await logEcommerceEvent(
          EventLogs.Events.ECOMMERCE_CONTACT_INFO_CONTINUE
        );
        router.push({
          name: RouteName.M3CXWaitValidationView,
          params: { countryCode: getCountryCode(), userTag: store.state.carts?.userTag },
        });
      }
    }
  }
};

const checkForm = () => {
  const isValidPhone = validatePhone();
  if (!isValidPhone) {
    return;
  }
  if (useFacebookFraudDetection.value && !facebookAccessed.value) {
    alertTypeFacebook.value = "alert-danger";
    alertMessageFacebook.value = t("FACEBOOK_SIGN_IN_REQUIRED");
    showAlertFacebook.value = true;
    return;
  }
  if (emailComponent.value.getInputFieldValue() && !validateEmail()) {
    return;
  }
  if (
    phoneVerificationRequired.value &&
    !isPhoneAlreadyVerified.value &&
    isValidPhone &&
    phoneComponent.value.validate() &&
    codeComponent.value.validate()
  ) {
    verifyPhone(
      store.state.carts.cartTag,
      codeComponent.value.getInputFieldValue()
    )
      .then(() => {
        readyToCheckPjVerified.value = true;
        handleCountrySpecificSettings();
      })
      .catch((error: AxiosError) => {
        alertMessageSMS.value = getAPIErrorMessage(error.response?.data, t);
        showAlertSMS.value = true;
        alertTypeSMS.value = "alert-danger";
      });
  } else {
    logEvent(EventLogs.Events.ID_REGISTRATION_DONE);
    logAction(ActionLogs.Events.ID_REGISTRATION_DONE);
    readyToCheckPjVerified.value = true;
    handleCountrySpecificSettings();
  }
};

const logout = () => {
  window.FB.logout();
  alertTypeFacebook.value = "alert-success";
  alertMessageFacebook.value = t("FACEBOOK_SIGNED_OUT_SUCCESS");
  showAlertFacebook.value = true;
};
const logInWithFacebook = () => {
  window.FB.login(
    (response) => {
      if (response.status === "connected") {
        const { authResponse } = response;
        saveFacebookData(
          store.state.carts.cartTag,
          authResponse.userID,
          authResponse.accessToken
        ).then(
          () => {
            facebookAccessed.value = true;
            logout();
          },
          (error: AxiosError) => {
            if (error) {
              setErrorMessage(getAPIErrorMessage(error.response?.data, t));
              logout();
            }
          }
        );
      } else {
        alertTypeFacebook.value = "alert-danger";
        alertMessageFacebook.value = t("FACEBOOK_NOT_CONNECTED");
        showAlertFacebook.value = true;
      }
    },
    {
      scope: "user_friends,email,user_likes,user_birthday",
      return_scopes: true,
      enable_profile_selector: true,
    }
  );
  return false;
};
const dismissAddressError = () => {
  addressError.value = false;
};

const submitAddress = () => {
  return new Promise((resolve, reject) => {
    if (!addressIsValid.value) {
      reject(false);
    }

    const address = {
      street1: newCustomerAddress.value.address,
      street2: "",
      neighborhood: newCustomerAddress.value.neighborhood,
      country: merchantCountry.value,
      city: newCustomerAddress.value.city,
      state: newCustomerAddress.value.state,
      stateCode: newCustomerAddress.value.stateCode,
      postalCode: newCustomerAddress.value.postalCode,
    };

    recordAddress(cartTag.value, address)
      .then(() => {
        logEvent(EventLogs.Events.CUSTOMER_ADDRESS_FIELDS_RECORDED);
        addressError.value = false;
        addressRecorded.value = true;
        resolve(true);
      })
      .catch((error) => {
        addressError.value = true;
        addressAlertType.value = "alert-danger";
        addressRecorded.value = false;
        addressErrorMessage.value = error.response.data.message;
        reject(false);
      });
  });
};
const updateCustomerAddress = (value: ICustomerAddress): void => {
  newCustomerAddress.value = { ...newCustomerAddress.value, ...value };
};

const handleBlockContinueForCeara = (shouldBlock: boolean): void => {
  shouldBlockContinueForCeara.value = shouldBlock;
};

const updatePoliticallyExposedPerson = (newValue: number) => {
  politicallyExposedPerson.value = newValue === 1;
  enableContinueButton();
};

const validateSourceOfFounds = () => {
  return sourceOfFundsRef.value.validate();
};

const creditCheckIsRequired = computed(() => {
  return (
    isBrazilSaleFlow() || isColombiaSelfServiceFlow() || isPhilippinesSaleFlow()
  );
});

const creditCheckPermissionLabel = computed(() => {
  if (isPhilippinesSaleFlow()) {
    return t("CREDIT_CHECK_CHECKBOX_PH_LABEL");
  }

  return t("CREDIT_CHECK_CHECKBOX_LABEL");
});

const creditCheckDisabled = ref(false);
const creditCheckChecked = ref(false);
const onCreditCheckClick = (checked: boolean) => {
  if (!checked) {
    return;
  }
  creditCheckDisabled.value = true;
  authorizeCreditCheck(cartTag.value, PermissionType.CHECKBOX)
    .then(() => {
      creditCheckChecked.value = true;
      logEvent(
        EventLogs.Events.CREDIT_CHECK_PERMISSION_CLICKED_CHECKBOX_OPTION
      );
    })
    .catch((error: AxiosError<IErrorResponse>) => {
      creditCheckDisabled.value = false;
      const errorResponse = error.response?.data;
      if (errorResponse) {
        setErrorMessage(getAPIErrorMessage(errorResponse, t));
      }
    });
};

const fraudCheckRef = ref<InstanceType<typeof FraudAssessmentCheck>>();
const requiresFraudAssessment: ComputedRef<boolean> = computed(() =>
  isIndonesiaSaleFlow() && !isRentalFlow.value
);
type IFraudAssessmentResult = "VALID" | "INVALID" | "UNAVAILABLE";
const fraudAssessmentResult: IFraudAssessmentResult | null = ref(null);
const handleFraudAssessmentResult = (newResult: IFraudAssessmentResult) => {
  fraudAssessmentResult.value = newResult;
  if (newResult === "VALID") {
    handleCountrySpecificSettings();
  }
};
const hasFraudulentAssessment = computed(
  () => fraudAssessmentResult.value === "INVALID"
);
const handleFraudAssessmentLoading = (enabled: boolean) => {
  loading.value = enabled;
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
h2 {
  color: $pj-color-black300;
}
p {
  font-size: 16px;
  color: $pj-color-gray;
}
.btn-phone {
  position: relative;
  top: 29px;
  height: 41px;
}
.input_tips {
  p {
    padding: 0 0 20px 0;
    font-size: 12px;
    color: $pj-color-gray;
  }
}
.client_email {
  padding: 0 0 36px 0;
}
.fb-accessed {
  margin-bottom: 10px;
  color: $pj-color-green;
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 34px;
}
.fb-label {
  margin-bottom: 0.5rem;
  color: #2c3e50;
}
.fb-login {
  width: 14rem;
  background-color: #3a76ea;
  padding: 0.65rem 0.5rem;
  color: white;
  font-weight: 700;
  font-size: 0.65rem;
  border-radius: 0.25rem;
  cursor: pointer;
  text-transform: uppercase;
  text-align: center;
  vertical-align: middle;
  border-color: transparent;
  margin-bottom: 10px;
}
.fb-login-logo {
  height: 1.25rem;
  padding-left: 0.1rem;
  vertical-align: middle;
}
.fb-inner {
  margin: auto;
}
</style>
